import type {
  ScaleLinear,
  ScaleLogarithmic,
  ScaleSymLog,
  ScaleTime,
} from 'd3-scale';

import { ThresholdIndicatorData } from '../components/threshold-bar/indicators/ThresholdIndicator.js';
import { getThresholdPixel } from '../components/threshold-bar/utils/get-threshold-pixel.ts.js';
import {
  DynamicNumericRangeThreshold,
  DynamicNumericThreshold,
  DynamicRangeThreshold,
  DynamicThreshold,
  NumericalThresholdData,
  StaticRangeThreshold,
  StaticThreshold,
  DateThresholdData,
} from '../types/thresholds.js';
import {
  isDynamic,
  isDynamicNumeric,
  isDynamicNumericRange,
  isDynamicRange,
  isStatic,
  isStaticRange,
} from '../types/type-guards.js';

const transformStatic = (
  data: StaticThreshold,
  yScale:
    | ScaleLinear<number, number>
    | ScaleLogarithmic<number, number>
    | ScaleSymLog<number, number>,
  chartWidth: number,
) => {
  const yScaleValue = getThresholdPixel(data.value, yScale);

  return [
    {
      x0: 0,
      x1: 0,
      y0: yScaleValue,
      y1: yScaleValue,
    },
    {
      x0: chartWidth,
      x1: chartWidth,
      y0: yScaleValue,
      y1: yScaleValue,
    },
  ];
};

const transformStaticRange = (
  data: StaticRangeThreshold,
  yScale:
    | ScaleLinear<number, number>
    | ScaleLogarithmic<number, number>
    | ScaleSymLog<number, number>,
  chartWidth: number,
) => {
  const yScaleMinValue = getThresholdPixel(data.min, yScale);
  const yScaleMaxValue = getThresholdPixel(data.max, yScale);

  return [
    {
      x0: 0,
      x1: 0,
      y0: yScaleMinValue,
      y1: yScaleMaxValue,
    },
    {
      x0: chartWidth,
      x1: chartWidth,
      y0: yScaleMinValue,
      y1: yScaleMaxValue,
    },
  ];
};

const transformDynamic = (
  data: DynamicThreshold,
  xScale: ScaleTime<number, number>,
  yScale:
    | ScaleLinear<number, number>
    | ScaleLogarithmic<number, number>
    | ScaleSymLog<number, number>,
) => {
  const orderedData = data.sort(
    (d1, d2) => d1.date.getTime() - d2.date.getTime(),
  );

  const [xMin, xMax] = xScale.domain();
  const [firstItem] = orderedData;

  if (xMin < firstItem.date) {
    orderedData.unshift({ value: firstItem.value, date: xMin });
  }

  const lastItem = orderedData[orderedData.length - 1];

  if (xMax > lastItem.date) {
    orderedData.push({
      value: lastItem.value,
      date: xMax,
    });
  }

  return data.map((thresholdData) => ({
    x0: xScale(thresholdData.date) ?? 0,
    x1: xScale(thresholdData.date) ?? 0,
    y0: getThresholdPixel(thresholdData.value, yScale),
    y1: getThresholdPixel(thresholdData.value, yScale),
  }));
};

const transformDynamicNumeric = (
  data: DynamicNumericThreshold,
  xScale:
    | ScaleLinear<number, number>
    | ScaleLogarithmic<number, number>
    | ScaleSymLog<number, number>,
  yScale:
    | ScaleLinear<number, number>
    | ScaleLogarithmic<number, number>
    | ScaleSymLog<number, number>,
) => {
  const orderedData = data.sort((d1, d2) => d1.point - d2.point);

  const [xMinDate, xMaxDate] = xScale.domain();
  const [firstItem] = orderedData;
  const xMin = xMinDate.valueOf();
  const xMax = xMaxDate.valueOf();

  if (xMin < firstItem.point) {
    orderedData.unshift({ value: firstItem.value, point: +xMin });
  }

  const lastItem = orderedData[orderedData.length - 1];

  if (xMax > lastItem.point) {
    orderedData.push({
      value: lastItem.value,
      point: xMax,
    });
  }

  return data.map((thresholdData) => ({
    x0: xScale(thresholdData.point) ?? 0,
    x1: xScale(thresholdData.point) ?? 0,
    y0: getThresholdPixel(thresholdData.value, yScale),
    y1: getThresholdPixel(thresholdData.value, yScale),
  }));
};

const transformDynamicRange = (
  data: DynamicRangeThreshold,
  xScale: ScaleTime<number, number>,
  yScale:
    | ScaleLinear<number, number>
    | ScaleLogarithmic<number, number>
    | ScaleSymLog<number, number>,
) => {
  const orderedData = data.sort(
    (d1, d2) => d1.date.getTime() - d2.date.getTime(),
  );

  const [xMin, xMax] = xScale.domain();
  const [firstItem] = orderedData;

  if (xMin < firstItem.date) {
    orderedData.unshift({
      min: firstItem.min,
      max: firstItem.max,
      date: xMin,
    });
  }

  const lastItem = orderedData[orderedData.length - 1];

  if (xMax > lastItem.date) {
    orderedData.push({
      min: lastItem.min,
      max: lastItem.max,
      date: xMax,
    });
  }

  return data.map((thresholdData) => ({
    x0: xScale(thresholdData.date) ?? 0,
    x1: xScale(thresholdData.date) ?? 0,
    y0: getThresholdPixel(thresholdData.min, yScale),
    y1: getThresholdPixel(thresholdData.max, yScale),
  }));
};

const transformDynamicNumericRange = (
  data: DynamicNumericRangeThreshold,
  xScale:
    | ScaleLinear<number, number>
    | ScaleLogarithmic<number, number>
    | ScaleSymLog<number, number>,
  yScale:
    | ScaleLinear<number, number>
    | ScaleLogarithmic<number, number>
    | ScaleSymLog<number, number>,
) => {
  const orderedData = data.sort((d1, d2) => d1.point - d2.point);

  const [xMinDate, xMaxDate] = xScale.domain();
  const [firstItem] = orderedData;

  const xMin = xMinDate.valueOf();
  const xMax = xMaxDate.valueOf();

  if (xMin < firstItem.point) {
    orderedData.unshift({
      min: firstItem.min,
      max: firstItem.max,
      point: xMin,
    });
  }

  const lastItem = orderedData[orderedData.length - 1];

  if (xMax > lastItem.point) {
    orderedData.push({
      min: lastItem.min,
      max: lastItem.max,
      point: xMax,
    });
  }

  return data.map((thresholdData) => ({
    x0: xScale(thresholdData.point) ?? 0,
    x1: xScale(thresholdData.point) ?? 0,
    y0: getThresholdPixel(thresholdData.min, yScale),
    y1: getThresholdPixel(thresholdData.max, yScale),
  }));
};

export const transformThresholdIntoIndicator = (
  data: DateThresholdData,
  chartWidth: number,
  yScale:
    | ScaleLinear<number, number>
    | ScaleLogarithmic<number, number>
    | ScaleSymLog<number, number>,
  xScale: ScaleTime<number, number>,
): ThresholdIndicatorData[] => {
  if (isStatic(data)) {
    return transformStatic(data, yScale, chartWidth);
  } else if (isStaticRange(data)) {
    return transformStaticRange(data, yScale, chartWidth);
  } else if (isDynamic(data)) {
    return transformDynamic(data, xScale, yScale);
  } else if (isDynamicRange(data)) {
    return transformDynamicRange(data, xScale, yScale);
  }

  return [];
};

export const transformHistogramThresholdIntoIndicator = (
  data: NumericalThresholdData,
  chartWidth: number,
  yScale:
    | ScaleLinear<number, number>
    | ScaleLogarithmic<number, number>
    | ScaleSymLog<number, number>,
  xScale: ScaleLinear<number, number> | ScaleSymLog<number, number>,
): ThresholdIndicatorData[] => {
  if (isStatic(data)) {
    return transformStatic(data, yScale, chartWidth);
  } else if (isStaticRange(data)) {
    return transformStaticRange(data, yScale, chartWidth);
  } else if (isDynamicNumeric(data)) {
    return transformDynamicNumeric(data, xScale, yScale);
  } else if (isDynamicNumericRange(data)) {
    return transformDynamicNumericRange(data, xScale, yScale);
  }

  return [];
};
